import axios from "axios";
import { appConfig } from "../config/app";

class HyperPayService {
  constructor() {
    this.baseURL = appConfig.DotNet_Api_Endpoint;
    // Always use test environment for now - change to production when going live
    this.isProduction = false; // Set to true when going live
  }

  /**
   * Get environment-specific configuration
   * @returns {Object} - Environment configuration
   */
  getEnvironmentConfig() {
    return {
      isProduction: this.isProduction,
      // Use the same base URL as your backend for consistency - EU region
      scriptBaseUrl: this.isProduction ? 'https://oppwa.com' : 'https://eu-test.oppwa.com',
      supportedBrands: 'VISA MASTER MADA APPLEPAY AMEX TAMARA',
      fastCheckoutEnabled: false, // Disabled to avoid conflicts
    };
  }

  /**
   * Initialize a payment checkout session
   * @param {Object} paymentData - Payment information
   * @returns {Promise<Object>} - Checkout ID and script URL
   */
  async initializePayment(paymentData) {
    try {
      const response = await axios.post(
        `${this.baseURL}/payments/hyperpay/checkout`,
        paymentData,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("HyperPay initialization error:", error);
      throw new Error("Failed to initialize payment session");
    }
  }

  /**
   * Get payment status
   * @param {string} checkoutId - Checkout ID from HyperPay
   * @returns {Promise<Object>} - Payment status
   */
  async getPaymentStatus(checkoutId) {
    try {
      // Use the callback endpoint to check status
      const response = await axios.post(
        `${this.baseURL}/payments/hyperpay/callback`,
        {
          CheckoutId: checkoutId,
          ResourcePath: `/v1/checkouts/${checkoutId}/payment`
        },
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("userToken")}`,
          },
        }
      );

      // Check if the response indicates success
      if (response.data && response.data.status) {
        console.log(`Frontend getPaymentStatus - Backend returned status: ${response.data.status}`);
        return {
          isSuccessful: response.data.status === 'successful',
          isFailed: response.data.status === 'failed',
          isPending: response.data.status === 'pending'
        };
      }

      console.log("Frontend getPaymentStatus - No status in response, defaulting to pending");
      return { isPending: true }; // Default to pending
    } catch (error) {
      console.error("HyperPay status check error:", error);
      return { isPending: true }; // Default to pending on error
    }
  }

  /**
   * Create HyperPay widget following the official documentation
   * @param {string} checkoutId - Checkout ID
   * @param {string} scriptUrl - Script URL for widget
   * @param {Object} options - Widget options
   * @returns {Promise<HTMLElement>} - Widget container
   */
  createWidget(checkoutId, scriptUrl, options = {}) {
    return new Promise((resolve, reject) => {
      // Clean up any existing widgets first
      this.destroyWidget();

      // Get environment-specific configuration
      const envConfig = this.getEnvironmentConfig();
      console.log('Environment configuration:', envConfig);

      // Create overlay
      const overlay = document.createElement("div");
      overlay.id = "hyperpay-overlay";
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 9999;
        display: flex;
        justify-content: center;
        align-items: center;
      `;

      // Create widget container
      const widgetContainer = document.createElement("div");
      widgetContainer.style.cssText = `
        background: white;
        padding: 30px;
        border-radius: 10px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        max-width: 500px;
        width: 90%;
        position: relative;
        max-height: 90vh;
        overflow-y: auto;
      `;

      // Create close button
      const closeButton = document.createElement("button");
      closeButton.innerHTML = "×";
      closeButton.style.cssText = `
        position: absolute;
        top: 10px;
        right: 15px;
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #666;
        z-index: 1;
      `;
      closeButton.onclick = () => {
        this.destroyWidget();
      };

      // Create title
      const title = document.createElement("h4");
      title.textContent = options.title || "Complete Payment";
      title.style.cssText = `
        text-align: center;
        margin-bottom: 20px;
        color: #040c4b;
        font-weight: bold;
      `;

      // Add amount display if provided
      if (options.amount) {
        const amountDisplay = document.createElement("div");
        amountDisplay.style.cssText = `
          text-align: center;
          font-size: 18px;
          font-weight: bold;
          color: #040c4b;
          margin-bottom: 20px;
          padding: 10px;
          background-color: #f8f9fa;
          border-radius: 5px;
        `;
        amountDisplay.textContent = `Total: ${options.currency || "SAR"} ${options.amount}`;
        widgetContainer.appendChild(amountDisplay);
      }

      // Create the shopperResultUrl based on current window location
      const shopperResultUrl = `${window.location.origin}/#/payment-result`;

      // Create form for HyperPay widget following the documentation
      const form = document.createElement("form");
      form.setAttribute("action", shopperResultUrl);
      form.setAttribute("class", "paymentWidgets");
      // Try using only basic card brands to avoid fast checkout conflicts
      const brands = "VISA MASTER MADA APPLEPAY AMEX TAMARA"; // Simplified, removed any potential fast checkout triggers
      form.setAttribute("data-brands", brands);
      
      // Add explicit data attributes to prevent fast checkout
      form.setAttribute("data-fastcheckout", "false");
      // form.setAttribute("data-applepay", "true");
      form.setAttribute("data-googlepay", "false");
      
      form.style.cssText = `
        margin-bottom: 20px;
      `;
      
      console.log(`Using brands: ${brands} (fast checkout disabled)`);
      console.log(`Environment: ${envConfig.isProduction ? 'production' : 'test'} - Script: ${envConfig.scriptBaseUrl}`);

      // Assemble the widget
      widgetContainer.appendChild(closeButton);
      widgetContainer.appendChild(title);
      widgetContainer.appendChild(form);
      overlay.appendChild(widgetContainer);
      document.body.appendChild(overlay);

      // Create and load the HyperPay widget script following the documentation
      const script = document.createElement("script");
      script.id = "hyperpay-script";
      
      // Use the scriptUrl parameter from backend if available, otherwise construct it
      let finalScriptUrl;
      
      if (scriptUrl && scriptUrl.includes('paymentWidgets.js')) {
        // If scriptUrl parameter contains the full URL, add checkoutId to it
        finalScriptUrl = `${scriptUrl}?checkoutId=${checkoutId}`;
        console.log(`Using backend-provided script URL: ${scriptUrl}`);
      } else {
        // Otherwise construct the URL with checkoutId using environment config
        finalScriptUrl = `${envConfig.scriptBaseUrl}/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
        console.log(`Constructing script URL from environment config: ${envConfig.scriptBaseUrl}`);
      }
      
      script.src = finalScriptUrl;
      script.setAttribute("crossorigin", "anonymous");
      
      console.log(`Final script URL: ${script.src}`);
      console.log(`Using checkoutId: ${checkoutId}`);
      console.log(`Supported brands: ${envConfig.supportedBrands || options.brands || 'VISA MASTER MADA APPLEPAY AMEX'}`);
      
      // Add integrity if provided in options
      if (options.integrity) {
        script.setAttribute("integrity", options.integrity);
      }
      
      // Configure widget options BEFORE loading the script
      window.wpwlOptions = {
        style: options.style || "card",
        locale: options.locale || "en",
        showCVVHint: options.showCVVHint !== false,
        showLabels: options.showLabels !== false,
        showPlaceholders: options.showPlaceholders !== false,
        
        // Completely disable fast checkout to avoid brand conflicts
        fastCheckout: {
          enabled: false,
          brands: [] // Empty array to ensure no brands are configured
        },
        
        // Disable all digital wallet payment methods
        applePay: {
          enabled: false
        },
        googlePay: {
          enabled: false
        },
        payPal: {
          enabled: false
        },
        
        // Only enable traditional card payments
        card: {
          enableCardHolderName: true,
          showCardHolderName: true
        },
        
        // Explicitly disable wallet-based payments
        wallets: {
          enabled: false
        },
        
        // Configure callback URLs for HyperPay
        onReady: () => {
          console.log("HyperPay widget ready - starting monitoring immediately");
          
          // Start payment monitoring immediately when widget is ready
          // This ensures we catch any early callbacks or direct processing
          setTimeout(() => {
            console.log("Starting immediate payment monitoring on widget ready");
            this.startPaymentMonitoring(checkoutId);
          }, 2000); // Give widget a moment to fully initialize
          
          // Aggressively hide any fast checkout elements that might still appear
          setTimeout(() => {
            const fastCheckoutSelectors = [
              '.wpwl-fastcheckout',
              '.wpwl-group-applePay', 
              '.wpwl-group-googlePay',
              '.wpwl-group-payPal',
              '.wpwl-brand-APPLEPAY', 
              '.wpwl-brand-GOOGLEPAY',
              '.wpwl-brand-PAYPAL',
              '[data-brand*="APPLE"]',
              '[data-brand*="GOOGLE"]',
              '[data-brand*="PAYPAL"]',
              '.wpwl-button-applePay',
              '.wpwl-button-googlePay',
              '.wpwl-button-payPal'
            ];
            
            fastCheckoutSelectors.forEach(selector => {
              const elements = document.querySelectorAll(selector);
              elements.forEach(element => {
                element.style.display = 'none';
                element.remove(); // Completely remove the element
              });
            });
            
            // Also check for any elements containing fast checkout text
            const allElements = document.querySelectorAll('*');
            allElements.forEach(element => {
              if (element.textContent && 
                  (element.textContent.includes('Apple Pay') || 
                   element.textContent.includes('Google Pay') ||
                   element.textContent.includes('PayPal'))) {
                element.style.display = 'none';
              }
            });
          }, 100);
          
          // Run the cleanup multiple times to catch dynamically loaded content
          setTimeout(() => {
            const fastCheckoutElements = document.querySelectorAll(
              '.wpwl-fastcheckout, .wpwl-group-applePay, .wpwl-group-googlePay, ' +
              '.wpwl-brand-APPLEPAY, .wpwl-brand-GOOGLEPAY'
            );
            fastCheckoutElements.forEach(element => {
              element.style.display = 'none';
              element.remove();
            });
          }, 500);
          
          setTimeout(() => {
            const fastCheckoutElements = document.querySelectorAll(
              '.wpwl-fastcheckout, .wpwl-group-applePay, .wpwl-group-googlePay, ' +
              '.wpwl-brand-APPLEPAY, .wpwl-brand-GOOGLEPAY'
            );
            fastCheckoutElements.forEach(element => {
              element.style.display = 'none';
              element.remove();
            });
          }, 1000);
          
          // Set up form submission monitoring
          setTimeout(() => {
            const paymentForm = document.querySelector('.paymentWidgets');
            if (paymentForm) {
              console.log("Setting up form submission monitoring");
              
              // Listen for form submission
              paymentForm.addEventListener('submit', (e) => {
                console.log("Payment form submitted - ensuring monitoring is active");
                
                // Store checkout information for result page
                sessionStorage.setItem('hyperPayCheckoutId', checkoutId);
                sessionStorage.setItem('paymentReferenceNo', sessionStorage.getItem('paymentReferenceNo'));
                
                // Show processing message immediately
                this.showProcessingMessage();
                
                // Ensure monitoring is running (it should already be from onReady)
                if (!this.paymentMonitorInterval) {
                  console.log("Starting payment monitoring from form submission");
                  setTimeout(() => {
                    this.startPaymentMonitoring(checkoutId);
                  }, 1000);
                } else {
                  console.log("Payment monitoring already running");
                }
                
                // Let the form submit naturally to HyperPay's result URL
              });
              
              // Also listen for button clicks as fallback
              const buttons = paymentForm.querySelectorAll('button[type="submit"], .wpwl-button');
              buttons.forEach(button => {
                button.addEventListener('click', (e) => {
                  console.log("Payment button clicked - ensuring monitoring is active");
                  
                  // Store checkout information for result page
                  sessionStorage.setItem('hyperPayCheckoutId', checkoutId);
                  sessionStorage.setItem('paymentReferenceNo', sessionStorage.getItem('paymentReferenceNo'));
                  
                  // Show processing message
                  this.showProcessingMessage();
                  
                  // Ensure monitoring is running
                  if (!this.paymentMonitorInterval) {
                    console.log("Starting payment monitoring from button click");
                    setTimeout(() => {
                      this.startPaymentMonitoring(checkoutId);
                    }, 1000);
                  }
                });
              });
            } else {
              console.log("Payment form not found, monitoring will rely on immediate start");
            }
          }, 1000);
        },
        
        onError: (error) => {
          console.error("HyperPay widget error:", error);
          
          // Handle specific fast checkout brand errors
          if (error && error.message && error.message.includes('fast checkout brands')) {
            console.log("Fast checkout brand error detected - this should be resolved with disabled fast checkout");
          }
          
          // Show error to user
          this.showErrorMessage(error.message || "Payment widget error occurred");
        }
      };
      
      script.onload = () => {
        console.log("HyperPay widget loaded successfully");
        resolve(overlay);
      };
      
      script.onerror = () => {
        console.error("Failed to load HyperPay widget");
        this.destroyWidget();
        reject(new Error("Failed to load payment widget"));
      };
      
      document.body.appendChild(script);
    });
  }

  /**
   * Create multiple card forms for brand promotion (as per HyperPay documentation)
   * @param {string} checkoutId - Checkout ID
   * @param {Array} formConfigs - Array of form configurations with brands
   * @param {Object} options - Widget options
   * @returns {Promise<HTMLElement>} - Widget container
   */
  createMultipleCardForms(checkoutId, formConfigs, options = {}) {
    return new Promise((resolve, reject) => {
      // Clean up any existing widgets first
      this.destroyWidget();

      // Create overlay
      const overlay = document.createElement("div");
      overlay.id = "hyperpay-overlay";
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 9999;
        display: flex;
        justify-content: center;
        align-items: center;
      `;

      // Create widget container
      const widgetContainer = document.createElement("div");
      widgetContainer.style.cssText = `
        background: white;
        padding: 30px;
        border-radius: 10px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        max-width: 600px;
        width: 90%;
        position: relative;
        max-height: 90vh;
        overflow-y: auto;
      `;

      // Create close button
      const closeButton = document.createElement("button");
      closeButton.innerHTML = "×";
      closeButton.style.cssText = `
        position: absolute;
        top: 10px;
        right: 15px;
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #666;
        z-index: 1;
      `;
      closeButton.onclick = () => {
        this.destroyWidget();
      };

      // Create title
      const title = document.createElement("h4");
      title.textContent = options.title || "Complete Payment";
      title.style.cssText = `
        text-align: center;
        margin-bottom: 20px;
        color: #040c4b;
        font-weight: bold;
      `;

      // Add amount display if provided
      if (options.amount) {
        const amountDisplay = document.createElement("div");
        amountDisplay.style.cssText = `
          text-align: center;
          font-size: 18px;
          font-weight: bold;
          color: #040c4b;
          margin-bottom: 20px;
          padding: 10px;
          background-color: #f8f9fa;
          border-radius: 5px;
        `;
        amountDisplay.textContent = `Total: ${options.currency || "SAR"} ${options.amount}`;
        widgetContainer.appendChild(amountDisplay);
      }

      // Create the shopperResultUrl based on current window location
      const shopperResultUrl = `${window.location.origin}/#/payment-result`;

      // Assemble the widget
      widgetContainer.appendChild(closeButton);
      widgetContainer.appendChild(title);

      // Create multiple forms as per documentation
      formConfigs.forEach((config, index) => {
        const form = document.createElement("form");
        form.setAttribute("action", shopperResultUrl);
        form.setAttribute("class", "paymentWidgets");
        form.setAttribute("data-brands", config.brands);
        form.style.cssText = `
          margin-bottom: 15px;
          padding: 15px;
          border: 1px solid #e0e0e0;
          border-radius: 5px;
        `;

        // Add form label if provided
        if (config.label) {
          const label = document.createElement("h6");
          label.textContent = config.label;
          label.style.cssText = `
            margin-bottom: 10px;
            color: #040c4b;
            font-weight: bold;
          `;
          widgetContainer.appendChild(label);
        }

        widgetContainer.appendChild(form);
      });

      overlay.appendChild(widgetContainer);
      document.body.appendChild(overlay);

      // Create and load the HyperPay widget script
      const script = document.createElement("script");
      script.id = "hyperpay-script";
      
      // Get environment-specific configuration
      const envConfig = this.getEnvironmentConfig();
      script.src = `${envConfig.scriptBaseUrl}/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
      script.setAttribute("crossorigin", "anonymous");
      
      console.log(`Loading HyperPay script from: ${script.src}`);
      console.log(`Environment: ${envConfig.isProduction ? 'production' : 'test'}`);
      
      // Add integrity if provided in options
      if (options.integrity) {
        script.setAttribute("integrity", options.integrity);
      }
      
      // Configure widget options
      window.wpwlOptions = {
        style: options.style || "card",
        locale: options.locale || "en",
        showCVVHint: options.showCVVHint !== false,
        showLabels: options.showLabels !== false,
        showPlaceholders: options.showPlaceholders !== false,
        
        // Configure card settings
        card: {
          enableCardHolderName: true,
          showCardHolderName: true
        },
        
        onReady: () => {
          console.log("HyperPay multiple forms widget ready");
          
          // Set up form submission monitoring for all forms
          setTimeout(() => {
            const paymentForms = document.querySelectorAll('.paymentWidgets');
            paymentForms.forEach(paymentForm => {
              paymentForm.addEventListener('submit', (e) => {
                console.log("Payment form submitted");
                
                // Store checkout information for result page
                sessionStorage.setItem('hyperPayCheckoutId', checkoutId);
                sessionStorage.setItem('paymentReferenceNo', sessionStorage.getItem('paymentReferenceNo'));
              });
              
              const buttons = paymentForm.querySelectorAll('button[type="submit"], .wpwl-button');
              buttons.forEach(button => {
                button.addEventListener('click', (e) => {
                  console.log("Payment button clicked");
                  
                  // Store checkout information for result page
                  sessionStorage.setItem('hyperPayCheckoutId', checkoutId);
                  sessionStorage.setItem('paymentReferenceNo', sessionStorage.getItem('paymentReferenceNo'));
                });
              });
            });
          }, 1000);
        },
        
        onError: (error) => {
          console.error("HyperPay widget error:", error);
          this.showErrorMessage(error.message || "Payment widget error occurred");
        }
      };
      
      script.onload = () => {
        console.log("HyperPay multiple forms widget loaded successfully");
        resolve(overlay);
      };
      
      script.onerror = () => {
        console.error("Failed to load HyperPay widget");
        this.destroyWidget();
        reject(new Error("Failed to load payment widget"));
      };
      
      document.body.appendChild(script);
    });
  }

  /**
   * Destroy the widget and clean up
   */
  destroyWidget() {
    console.log("Destroying HyperPay widget");
    
    // Stop payment monitoring
    this.stopPaymentMonitoring();
    
    // Remove overlay
    const overlay = document.getElementById("hyperpay-overlay");
    if (overlay) {
      console.log("Removing HyperPay overlay");
      try {
        document.body.removeChild(overlay);
      } catch (e) {
        console.log("Overlay already removed or not found");
      }
    }

    // Remove script
    const script = document.getElementById("hyperpay-script");
    if (script) {
      console.log("Removing HyperPay script");
      try {
        document.body.removeChild(script);
      } catch (e) {
        console.log("Script already removed or not found");
      }
    }

    // Clean up global variables
    if (window.wpwlOptions) {
      console.log("Cleaning up HyperPay global variables");
      delete window.wpwlOptions;
    }

    // Additional cleanup for any remaining HyperPay elements
    const hyperPayElements = document.querySelectorAll('.wpwl-container, .wpwl-wrapper, .paymentWidgets');
    hyperPayElements.forEach(element => {
      try {
        element.remove();
      } catch (e) {
        console.log("Element already removed:", e);
      }
    });
  }

  /**
   * Force cleanup of all HyperPay elements (emergency cleanup)
   */
  forceCleanup() {
    console.log("Force cleaning up all HyperPay elements");
    
    // Stop any monitoring
    this.stopPaymentMonitoring();
    
    // Remove all possible HyperPay elements
    const selectors = [
      '#hyperpay-overlay',
      '#hyperpay-script',
      '.wpwl-container',
      '.wpwl-wrapper',
      '.paymentWidgets',
      '.wpwl-form',
      '.wpwl-group',
      '.wpwl-button',
      '[class*="wpwl"]',
      '[id*="wpwl"]'
    ];
    
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        try {
          element.remove();
          console.log(`Removed element: ${selector}`);
        } catch (e) {
          console.log(`Could not remove element: ${selector}`, e);
        }
      });
    });
    
    // Clean up global variables
    if (window.wpwlOptions) {
      delete window.wpwlOptions;
    }
    
    // Clean up any HyperPay globals
    const hyperPayGlobals = Object.keys(window).filter(key => 
      key.toLowerCase().includes('wpwl') || key.toLowerCase().includes('hyperpay')
    );
    
    hyperPayGlobals.forEach(key => {
      try {
        delete window[key];
        console.log(`Cleaned up global: ${key}`);
      } catch (e) {
        console.log(`Could not clean up global: ${key}`, e);
      }
    });
  }

  /**
   * Validate payment result
   * @param {string} checkoutId - Checkout ID
   * @param {Object} result - Payment result from HyperPay
   * @returns {boolean} - Whether payment was successful
   */
  validatePaymentResult(checkoutId, result) {
    // HyperPay successful payment codes
    const successCodes = [
      /^(000\.000\.|000\.100\.1|000\.[36])/,
      /^(000\.400\.0[^3]|000\.400\.100)/
    ];

    if (result && result.code) {
      return successCodes.some(pattern => pattern.test(result.code));
    }

    return false;
  }

  /**
   * Handle payment callback
   * @param {URLSearchParams} params - URL parameters from callback
   * @returns {Promise<Object>} - Payment result
   */
  async handlePaymentCallback(params) {
    const checkoutId = params.get('id');
    const resourcePath = params.get('resourcePath');

    if (!checkoutId || !resourcePath) {
      throw new Error("Invalid payment callback parameters");
    }

    try {
      // Verify payment with backend - Use correct field names that match your backend
      const response = await axios.post(
        `${this.baseURL}/payments/hyperpay/callback`,
        {
          CheckoutId: checkoutId,  // Match your backend's expected field name
          ResourcePath: resourcePath,
        },
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("userToken")}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Payment callback error:", error);
      throw new Error("Payment verification failed");
    }
  }

  /**
   * Start monitoring payment status
   * @param {string} checkoutId - Checkout ID to monitor
   */
  startPaymentMonitoring(checkoutId) {
    console.log("Starting payment monitoring for:", checkoutId);
    
    // Prevent multiple monitoring intervals
    if (this.paymentMonitorInterval) {
      console.log("Payment monitoring already running, skipping duplicate start");
      return;
    }
    
    let attempts = 0;
    const maxAttempts = 120; // Monitor for up to 6 minutes (120 * 3 seconds) - increased for better coverage
    let consecutiveEarlyCallbacks = 0;
    let earlyCallbackDetected = false;
    
    // Check payment status every 3 seconds
    this.paymentMonitorInterval = setInterval(async () => {
      attempts++;
      
      try {
        const status = await this.getPaymentStatus(checkoutId);
        console.log(`Payment status check (attempt ${attempts}):`, status);
        
        // Check if status shows completion
        if (status && status.isSuccessful) {
          console.log("Payment successful! Redirecting...");
          this.stopPaymentMonitoring();
          
          // Show success message briefly
          this.showSuccessMessage();
          
          // Redirect to result page after 2 seconds
          setTimeout(() => {
            this.destroyWidget();
            window.location.href = `#/payment-result?ref=${checkoutId}&status=success`;
          }, 2000);
          return;
        } 
        
        if (status && status.isFailed) {
          console.log("Payment failed! Redirecting...");
          this.stopPaymentMonitoring();
          
          // Redirect to result page with failure status
          setTimeout(() => {
            this.destroyWidget();
            window.location.href = `#/payment-result?ref=${checkoutId}&status=failed`;
          }, 1000);
          return;
        }
        
        // If still pending, track consecutive early callbacks
        if (status && status.isPending) {
          consecutiveEarlyCallbacks++;
          console.log(`Frontend monitoring - Payment still pending (${consecutiveEarlyCallbacks} consecutive pending responses)`);
          
          // Detect if we're getting early callbacks (200.300.404 pattern)
          if (consecutiveEarlyCallbacks >= 3) {
            if (!earlyCallbackDetected) {
              earlyCallbackDetected = true;
              console.log("Early callback pattern detected - payment may still be in progress");
              
              // Update processing message to indicate we're waiting for payment completion
              this.updateProcessingMessage("Payment in progress, please complete the transaction...");
            }
          }
          
          // If we've had many early callbacks, reduce polling frequency to be server-friendly
          // But don't stop monitoring completely as payment might still complete
          if (consecutiveEarlyCallbacks > 15) {
            // Check every other attempt after many early callbacks
            if (attempts % 2 === 0) {
              console.log(`Reducing polling frequency (attempt ${attempts}) due to ${consecutiveEarlyCallbacks} consecutive early callbacks`);
              return;
            }
          }
          
          // After many early callbacks, increase interval to every 6 seconds
          if (consecutiveEarlyCallbacks > 25) {
            if (attempts % 3 !== 0) {
              return;
            }
          }
        } else {
          // Reset counter if we get a different response
          consecutiveEarlyCallbacks = 0;
        }
        
        // Extended timeout for early callback scenarios
        const effectiveMaxAttempts = earlyCallbackDetected ? 200 : maxAttempts;
        
        // Check if we've reached max attempts
        if (attempts >= effectiveMaxAttempts) {
          console.log("Payment monitoring timeout reached - stopping automatic checks");
          this.stopPaymentMonitoring();
          
          // Show timeout message but don't automatically redirect
          this.showTimeoutMessage(checkoutId);
          return;
        }
        
      } catch (error) {
        console.error("Error monitoring payment status:", error);
        
        // Be more lenient with errors during early callback scenarios
        const maxErrors = earlyCallbackDetected ? 10 : 5;
        
        // If we get too many consecutive errors, stop monitoring
        if (attempts >= maxErrors) {
          console.log("Too many errors, stopping payment monitoring");
          this.stopPaymentMonitoring();
          this.showErrorMessage("Unable to check payment status. Please refresh the page.");
        }
      }
    }, 3000);
  }

  /**
   * Stop payment monitoring
   */
  stopPaymentMonitoring() {
    if (this.paymentMonitorInterval) {
      clearInterval(this.paymentMonitorInterval);
      this.paymentMonitorInterval = null;
    }
  }

  /**
   * Show processing message
   */
  showProcessingMessage() {
    const overlay = document.getElementById("hyperpay-overlay");
    if (overlay) {
      // Find the existing widget container
      const existingContainer = overlay.querySelector("div");
      if (existingContainer) {
        // Add processing message to existing container
        const processingDiv = document.createElement("div");
        processingDiv.id = "processing-message";
        processingDiv.style.cssText = `
          background-color: #e7f3ff;
          color: #040c4b;
          padding: 15px;
          margin-top: 20px;
          border-radius: 5px;
          border: 1px solid #b3d9ff;
          text-align: center;
          animation: pulse 1.5s ease-in-out infinite;
        `;
        processingDiv.innerHTML = `
          <div style="display: flex; align-items: center; justify-content: center; gap: 10px;">
            <div style="
              width: 20px; 
              height: 20px; 
              border: 2px solid #040c4b; 
              border-top: 2px solid transparent; 
              border-radius: 50%; 
              animation: spin 1s linear infinite;
            "></div>
            <span>Processing your payment...</span>
          </div>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }
          </style>
        `;
        
        // Remove any existing processing message
        const existingProcessing = existingContainer.querySelector("#processing-message");
        if (existingProcessing) {
          existingProcessing.remove();
        }
        
        existingContainer.appendChild(processingDiv);
      }
    }
  }

  /**
   * Update processing message with custom text
   * @param {string} message - Custom message to show
   */
  updateProcessingMessage(message = "Processing your payment...") {
    const processingDiv = document.getElementById("processing-message");
    if (processingDiv) {
      const spinner = processingDiv.querySelector("div");
      if (spinner) {
        const textSpan = spinner.querySelector("span");
        if (textSpan) {
          textSpan.textContent = message;
        }
      }
    }
  }

  /**
   * Show timeout message
   */
  showTimeoutMessage(checkoutId) {
    const overlay = document.getElementById("hyperpay-overlay");
    if (overlay) {
      overlay.innerHTML = `
        <div style="
          background: white;
          padding: 40px;
          border-radius: 10px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          max-width: 450px;
          width: 90%;
          text-align: center;
        ">
          <div style="color: #ffc107; font-size: 50px; margin-bottom: 20px;">⏰</div>
          <h3 style="color: #040c4b; margin-bottom: 15px;">Payment Status Check Timeout</h3>
          <p style="color: #666; margin-bottom: 25px; line-height: 1.5;">
            We're still processing your payment. This may take a few more moments.
          </p>
          <div style="display: flex; gap: 15px; justify-content: center;">
            <button onclick="window.location.reload()" style="
              background-color: #040c4b;
              color: white;
              border: none;
              padding: 12px 25px;
              border-radius: 5px;
              cursor: pointer;
              font-size: 14px;
            ">Refresh Page</button>
            <button onclick="window.location.href='#/payment-result?ref=${checkoutId}&status=pending'" style="
              background-color: #6c757d;
              color: white;
              border: none;
              padding: 12px 25px;
              border-radius: 5px;
              cursor: pointer;
              font-size: 14px;
            ">Check Status</button>
          </div>
          <p style="color: #999; font-size: 12px; margin-top: 20px;">
            Reference: ${checkoutId}
          </p>
        </div>
      `;
    }
  }

  /**
   * Show success message
   */
  showSuccessMessage() {
    const overlay = document.getElementById("hyperpay-overlay");
    if (overlay) {
      overlay.innerHTML = `
        <div style="
          background: white;
          padding: 40px;
          border-radius: 10px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          max-width: 400px;
          width: 90%;
          text-align: center;
        ">
          <div style="color: #28a745; font-size: 60px; margin-bottom: 20px;">✓</div>
          <h3 style="color: #040c4b; margin-bottom: 10px;">Payment Successful!</h3>
          <p style="color: #666; margin-bottom: 20px;">Your payment has been processed successfully.</p>
          <div style="color: #666;">Redirecting...</div>
        </div>
      `;
    }
  }

  /**
   * Show error message
   */
  showErrorMessage(message) {
    const overlay = document.getElementById("hyperpay-overlay");
    if (overlay) {
      const widgetContainer = overlay.querySelector("div");
      if (widgetContainer) {
        // Create error message div
        const errorDiv = document.createElement("div");
        errorDiv.style.cssText = `
          background-color: #f8d7da;
          color: #721c24;
          padding: 10px;
          margin-top: 15px;
          border-radius: 5px;
          border: 1px solid #f5c6cb;
          text-align: center;
        `;
        errorDiv.textContent = message;
        
        // Remove any existing error messages
        const existingError = widgetContainer.querySelector(".hyperpay-error");
        if (existingError) {
          existingError.remove();
        }
        
        errorDiv.className = "hyperpay-error";
        widgetContainer.appendChild(errorDiv);
      }
    }
  }

  /**
   * Handle payment success callback
   * @param {string} checkoutId - Checkout ID
   * @param {Object} data - Payment success data
   */
  handlePaymentSuccess(checkoutId, data) {
    console.log("Payment success:", data);
    this.stopPaymentMonitoring();
    this.destroyWidget();
    
    // Show success message briefly before redirect
    this.showSuccessMessage();
    
    // Redirect to result page after 2 seconds
    setTimeout(() => {
      window.location.href = `#/payment-result?ref=${checkoutId}&status=success`;
    }, 2000);
  }

  /**
   * Handle payment error callback
   * @param {string} checkoutId - Checkout ID
   * @param {Object} error - Payment error data
   */
  handlePaymentError(checkoutId, error) {
    console.log("Payment error:", error);
    this.stopPaymentMonitoring();
    this.destroyWidget();
    
    // Redirect to result page with error
    window.location.href = `#/payment-result?ref=${checkoutId}&status=failed&error=${encodeURIComponent(error.message || 'Payment failed')}`;
  }
}

const hyperPayService = new HyperPayService();
export default hyperPayService;
